
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  ArrowUpRight,
  Search,
  Sparkles,
  Clock,
  RefreshCw,
  X,
  CalendarCheck,
} from "lucide-react";
import axios from "axios";

// UCTC Brand Colors
const UCTC_BLUE = "#014864";
const UCTC_CYAN = "#6BCCF2";
const UCTC_GREEN = "#115e32";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0 },
};

const formatDate = (date) => {
  if (!date) return "Date TBA";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "Date TBA";
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Loading Skeleton
const EventSkeleton = () => (
  <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
    <div className="h-56 animate-pulse bg-gradient-to-r from-cyan-100 via-slate-100 to-cyan-100" />

    <div className="space-y-4 p-5">
      <div className="h-5 w-3/4 animate-pulse rounded bg-slate-200" />

      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200" />

      <div className="h-10 animate-pulse rounded-lg bg-slate-100" />
    </div>
  </div>
);

export default function AllEvents({
  logoSrc = "/img/logo.png",
  accent = UCTC_GREEN,
  pageTitle = "Events and Activities | University of Creative Technology Chittagong",
}) {
  const [news, setNews] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch Events
  const newsPost = async () => {
    setLoading(true);
    setError(null);

    try {
      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/all-events", {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
      });

      const data = Array.isArray(response.data)
        ? response.data.slice(0, 48)
        : [];

      setNews(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load events. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    newsPost();
  }, []);

  // Page Title
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // Search Events
  const filteredNews = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return news;

    return news.filter((item) => {
      const searchable = [
        item.name,
        item.description,
        item.date,
        item.start_date,
        item.end_date,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [news, search]);

  return (
    <main className="min-h-screen overflow-hidden bg-gradient-to-br from-[#e0f7fa] via-white to-[#e6f4ea]">
      {/* ================================================== */}
      {/* HERO SECTION */}
      {/* ================================================== */}

      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#6BCCF2] via-[#e0f7fa] to-[#f0fdf4]">
        {/* Background Decoration */}

        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/40 blur-3xl"
          />

          <motion.div
            animate={{
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-20 bottom-0 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl"
          />

          {/* Dotted Pattern */}

          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "radial-gradient(#014864 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          />
        </div>

        {/* Floating Shapes */}

        <motion.div
          animate={{
            y: [0, -18, 0],
            rotate: [0, 8, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute right-[12%] top-20 hidden h-20 w-20 rounded-2xl border border-[#014864]/20 bg-white/30 backdrop-blur-md lg:block"
        />

        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-24 left-[10%] hidden h-14 w-14 rounded-full border border-emerald-500/20 bg-emerald-300/20 lg:block"
        />

        {/* Hero Content */}

        <div className="relative mx-auto max-w-7xl px-6 pb-32 pt-24 text-center md:px-12 md:pb-36 md:pt-32">
          {/* Badge */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#014864]/10 bg-white/60 px-5 py-2 text-sm font-semibold text-[#014864] shadow-sm backdrop-blur-md"
          >
            <Sparkles size={16} className="text-emerald-700" />

            Campus Life & Experiences
          </motion.div>

          {/* Heading */}

          <motion.h1
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mx-auto max-w-5xl text-4xl font-extrabold leading-tight tracking-tight text-[#014864] sm:text-5xl md:text-7xl"
          >
            Discover Our

            <span className="mt-2 block bg-gradient-to-r from-[#115e32] via-[#0e7490] to-[#014864] bg-clip-text text-transparent">
              Events & Activities
            </span>
          </motion.h1>

          {/* Description */}

          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mx-auto mt-7 max-w-2xl text-base leading-8 text-slate-700 md:text-lg"
          >
            Explore the moments that bring our university community together.
            Discover academic celebrations, cultural festivals, seminars,
            workshops, and unforgettable campus experiences.
          </motion.p>

          {/* Buttons */}

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4"
          >
            <a
              href="#events"
              className="group inline-flex items-center gap-3 rounded-xl bg-[#115e32] px-7 py-4 font-bold text-white shadow-lg shadow-emerald-900/20 transition duration-300 hover:-translate-y-1 hover:bg-[#0d4826]"
            >
              Explore Events

              <ArrowUpRight
                size={20}
                className="transition group-hover:rotate-45"
              />
            </a>

            <div className="flex items-center gap-3 rounded-xl border border-[#014864]/10 bg-white/60 px-6 py-4 text-[#014864] shadow-sm backdrop-blur-md">
              <CalendarDays className="text-[#115e32]" size={22} />

              <div className="text-left">
                <p className="text-xs text-slate-500">
                  Campus Moments
                </p>

                <p className="font-semibold">
                  Memories That Matter
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Curved Bottom */}

        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 100"
            className="h-16 w-full md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,60 C360,120 900,0 1440,60 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* ================================================== */}
      {/* EVENTS SECTION */}
      {/* ================================================== */}

      <section
        id="events"
        className="relative mx-auto max-w-7xl px-5 pb-24 pt-10 md:px-12"
      >
        {/* Section Heading */}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#115e32]">
              <span className="h-1 w-8 rounded-full bg-[#115e32]" />

              University Highlights
            </div>

            <h2 className="text-3xl font-extrabold tracking-tight text-[#014864] md:text-4xl">
              Latest Events

              <span className="text-[#115e32]">
                {" "} & News
              </span>
            </h2>

            <p className="mt-3 max-w-xl text-slate-600">
              Stay connected with the latest happenings and memorable
              experiences at UCTC.
            </p>
          </div>

          {/* Event Counter */}

          <div className="flex items-center gap-3 rounded-xl border border-cyan-100 bg-white px-5 py-3 shadow-sm">
            <CalendarDays className="text-[#115e32]" size={20} />

            <div>
              <p className="text-xs font-medium text-slate-400">
                Events Available
              </p>

              <p className="text-xl font-bold text-[#014864]">
                {loading ? "..." : filteredNews.length}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Search */}

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10"
        >
          <div className="relative max-w-lg">
            <Search
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              placeholder="Search events, activities, dates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-2xl border border-cyan-100 bg-white py-4 pl-12 pr-12 text-sm text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#6BCCF2] focus:ring-4 focus:ring-[#6BCCF2]/20"
            />

            {search && (
              <button
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-[#115e32]"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Loading Skeleton */}

        {loading && (
          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <EventSkeleton key={index} />
            ))}
          </div>
        )}

        {/* Error State */}

        {!loading && error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-red-100 bg-white p-10 text-center shadow-sm"
          >
            <p className="font-semibold text-red-700">
              {error}
            </p>

            <button
              onClick={newsPost}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#115e32] px-5 py-3 text-white transition hover:bg-[#0d4826]"
            >
              <RefreshCw size={16} />

              Try Again
            </button>
          </motion.div>
        )}

        {/* Event Cards */}

        {!loading && !error && (
          <AnimatePresence mode="wait">
            {filteredNews.length > 0 ? (
              <motion.div
                key={search}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              >
                {filteredNews.map((item, index) => (
                  <motion.article
                    key={item.id}
                    variants={fadeUp}
                    initial="hidden"
                    animate="visible"
                    transition={{
                      duration: 0.55,
                      delay: Math.min(index * 0.06, 0.4),
                    }}
                    whileHover={{ y: -8 }}
                    className="group overflow-hidden rounded-2xl border border-cyan-100/70 bg-white shadow-sm transition-shadow duration-300 hover:shadow-2xl hover:shadow-[#014864]/10"
                  >
                    {/* Event Image */}

                    <div className="relative h-56 overflow-hidden bg-cyan-50">
                      <img
                        src={
                          item.images
                            ? `${import.meta.env.VITE_API_SOURCE_URL}${item.images}`
                            : "https://via.placeholder.com/600x400?text=UCTC+Event"
                        }
                        alt={item.name || "UCTC Event"}
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                      />

                      {/* Image Overlay */}

                      <div className="absolute inset-0 bg-gradient-to-t from-[#014864]/70 via-transparent to-transparent opacity-80" />

                      {/* Date Badge */}

                      <div className="absolute left-4 top-4 flex items-center gap-2 rounded-lg border border-white/50 bg-white/95 px-3 py-2 text-xs font-bold text-[#014864] shadow-lg backdrop-blur-md">
                        <CalendarDays size={14} />

                        {formatDate(item.start_date)}
                      </div>

                      {/* Event Date */}

                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-xs font-medium text-white">
                          <Clock size={13} />

                          <span>
                            {formatDate(item.start_date)} -{" "}
                            {formatDate(item.end_date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Event Content */}

                    <div className="flex min-h-[220px] flex-col p-5">
                      <h3 className="line-clamp-2 text-lg font-bold leading-7 text-[#014864] transition-colors group-hover:text-[#115e32]">
                        {item.name || "Untitled Event"}
                      </h3>

                      {item.description && (
                        <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-500">
                          {item.description
                            .replace(/<[^>]*>/g, " ")
                            .trim()}
                        </p>
                      )}

                      {/* Read More */}

                      <div className="mt-auto pt-5">
                        <Link
                          to={`/events/${item.id}`}
                          className="group/link flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold text-[#115e32] transition duration-300 hover:border-[#115e32] hover:bg-[#115e32] hover:text-white"
                        >
                          <span>Explore Event</span>

                          <ArrowUpRight
                            size={18}
                            className="transition-transform group-hover/link:translate-x-1 group-hover/link:-translate-y-1"
                          />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-dashed border-cyan-200 bg-white px-6 py-20 text-center shadow-sm"
              >
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-50">
                  <Search className="text-[#014864]" size={28} />
                </div>

                <h3 className="text-xl font-bold text-[#014864]">
                  No events found
                </h3>

                <p className="mt-2 text-sm text-slate-500">
                  Try another keyword to discover university activities.
                </p>

                <button
                  onClick={() => setSearch("")}
                  className="mt-6 rounded-xl bg-[#115e32] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#0d4826]"
                >
                  Show All Events
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </section>

      {/* ================================================== */}
      {/* FOOTER CTA */}
      {/* ================================================== */}

      <section className="relative overflow-hidden bg-gradient-to-r from-[#014864] to-[#115e32] px-6 py-16 text-center">
        {/* Decorative Background */}

        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.15, 0.3, 0.15],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -right-20 -top-32 h-96 w-96 rounded-full bg-[#6BCCF2] blur-3xl"
        />

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <CalendarCheck
            className="mx-auto mb-5 text-[#6BCCF2]"
            size={32}
          />

          <h2 className="text-2xl font-extrabold text-white md:text-4xl">
            Every Event Creates a Memory
          </h2>

          <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-cyan-50/80 md:text-base">
            At UCTC, every gathering is an opportunity to learn, connect,
            celebrate, and grow together.
          </p>

          <a
            href="#events"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 font-bold text-[#014864] transition duration-300 hover:-translate-y-1 hover:bg-[#6BCCF2]"
          >
            Discover More

            <ArrowUpRight size={18} />
          </a>
        </motion.div>
      </section>
    </main>
  );
}
